import { useCallback, useEffect, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  mathsEngine,
  toMathsView,
  type MathsAction,
  type MathsState,
  type MathsView,
  type SkillLevels,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO = 'solo'
const PLAYER = { id: SOLO, displayName: 'You', userId: null } as const

interface Round {
  readonly seed: string
  readonly state: MathsState
  readonly actions: readonly MathsAction[]
}

type RoundEvent = { type: 'answer'; action: MathsAction; now: number }

function start(seed: string, levels: SkillLevels, now: number): Round {
  return {
    seed,
    state: mathsEngine.createInitialState({ levels, startedAt: now }, [PLAYER], createRng(seed)),
    actions: [],
  }
}

/** Pure, so React re-running it cannot record a round twice. */
function reduceRound(round: Round, event: RoundEvent): Round {
  const outcome = applyAction(mathsEngine, round.state, event.action, {
    playerId: SOLO,
    now: event.now,
  })

  if (!outcome.ok) return round

  return { ...round, state: outcome.state, actions: [...round.actions, event.action] }
}

export interface Maths {
  readonly view: MathsView
  readonly answer: (value: number) => void
  readonly again: () => void
  readonly finished: boolean
  /** The milestone this round crossed, if it crossed one. Null the rest of the time. */
  readonly milestone: number | null
}

/**
 * Solo maths, running the shared engine in the browser.
 *
 * The starting levels come from the server, so Tuesday continues from Monday.
 * The moves and the seed are kept so the finished round can be replayed
 * server-side — the client never reports its own score, and here it does not
 * report its own levels either.
 */
/**
 * Solo maths, running the shared engine in the browser.
 *
 * The round comes from the server: the seed, and the levels it was dealt from.
 * Those levels are the point. Question selection is weighted by level, so a
 * round can only be checked afterwards against the levels it was *dealt* from —
 * and they move the moment a round is recorded. Carrying them on the round
 * rather than fetching them separately is what stops the two drifting apart.
 */
export function useMaths(
  round: DealtRound<{ levels: SkillLevels }>,
  onFinished: () => void,
): Maths {
  const [state, dispatch] = useReducer(reduceRound, round.seed, (seed) =>
    start(seed, round.config.levels, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = state.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    // Every answer, with the moment it was given, is already at the server.
    // The timing is not incidental: fluency is judged by the clock, and a round
    // reconstructed without it scores every answer as instant.
    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The round
        // is not spent, so it can still be finished from another device — and
        // asking now would only have it refused.
        if (!told) return undefined

        return api('/api/plays/maths', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She answered the
      // questions either way.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  return {
    view: toMathsView(state.state),
    answer: useCallback(
      (value: number) => {
        const now = Date.now()
        const action = { type: 'answer', value } as const

        dispatch({ type: 'answer', action, now })
        // Never earlier than the answer before it: a device whose clock is
        // corrected mid-round would otherwise send times that run backwards.
        moves.record(action, Math.max(0, now - state.state.startedAt))
      },
      [moves, state.state.startedAt],
    ),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
